/**
 * MODULE: platform/browser/frontend/utils/formatBrowserError.ts
 * PURPOSE: User-friendly browser session error messages.
 */

/** True when Steel/Chromium failed to launch (Azure VM, resource limits, bad image). */
export function isSteelChromiumLaunchError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('steel_chromium_launch_failed') ||
    lower.includes('launch_failed') ||
    lower.includes('failed to launch the browser')
  );
}

/** True when tenant hit concurrent browser session quota. */
export function isMaxSessionsError(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('maximum') && lower.includes('session');
}

/** True when the remote Browserbase/Steel session is no longer reachable. */
export function isBrowserSessionDeadError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('410') ||
    lower.includes('gone') ||
    lower.includes('session not running') ||
    lower.includes('session not found') ||
    lower.includes('has been closed') ||
    lower.includes('target page, context or browser') ||
    (lower.includes('connect_over_cdp') &&
      (lower.includes('websocket') || lower.includes('has been closed')))
  );
}

/** True when the user can retry the last instruction without reconnecting. */
export function isRecoverableBrowserError(message: string): boolean {
  if (isBrowserSessionDeadError(message)) {
    return false;
  }
  const lower = message.toLowerCase();
  if (lower.includes('maximum') && lower.includes('session')) {
    return false;
  }
  if (lower.includes('no browser provider') || lower.includes('browserbase_api_key')) {
    return false;
  }
  if (isSteelChromiumLaunchError(message)) {
    return false;
  }
  return (
    lower.includes('sign-in required') ||
    lower.includes('action failed') ||
    lower.includes('llm error') ||
    lower.includes('failed to open start url') ||
    lower.includes('navigation blocked')
  );
}

export function formatBrowserSessionError(message: string): string {
  const lower = message.toLowerCase();
  if (isSteelChromiumLaunchError(message)) {
    return 'Remote Chrome could not start. Your admin should check steel-browser logs, restart the container, or upgrade the Azure VM CPU/RAM. You can retry after the service is fixed.';
  }
  if (isBrowserSessionDeadError(message)) {
    return 'Browser session ended. Reconnect to continue — your last command can retry automatically.';
  }
  if (lower.includes('no browser provider') || lower.includes('steel_api_url') || lower.includes('browserbase_api_key')) {
    return 'Remote browser is not configured. Ask your admin to set STEEL_API_URL (or BROWSERBASE_API_KEY) on platform-service, then restart the container.';
  }
  if (lower.includes('maximum') && lower.includes('session')) {
    return 'You have the maximum number of browser sessions open. Click Release stale sessions below, or end a session and try again.';
  }
  return message;
}

/** Format act/SSE errors — trim verbose CDP call logs for chat display. */
export function formatBrowserActError(message: string): string {
  if (isBrowserSessionDeadError(message)) {
    return formatBrowserSessionError(message);
  }
  const lower = message.toLowerCase();
  if (
    lower.includes('locator.click') ||
    lower.includes('intercepts pointer events') ||
    lower.includes('timeout') && lower.includes('exceeded')
  ) {
    return 'Action failed: could not click the target element (it may be covered). After typing in a search box, press Enter instead of clicking suggestions — or simplify the instruction.';
  }
  if (message.length > 180) {
    const callLogIdx = message.search(/call log:/i);
    if (callLogIdx > 0) {
      return `${message.slice(0, callLogIdx).trim()} Try again or simplify the instruction.`;
    }
    const actionFailed = message.match(/^Action failed:[^.]+(?:\.|$)/i)?.[0]?.trim();
    if (actionFailed) {
      return `${actionFailed} Try again or simplify the instruction.`;
    }
    return `${message.slice(0, 180).trim()}… Try again or simplify the instruction.`;
  }
  return formatBrowserSessionError(message);
}
